import { MapPin } from "lucide-react";
import SurfaceCard from "@/components/primitives/SurfaceCard";
import { stateName, type OfficesDisplay } from "@/lib/officesDisplay";
import { CLOUD_FOOTPRINT_LINE } from "@/lib/workModel";

export interface OfficeStateGroup {
  /** Two-letter state code (uppercased), or "" when the entry carried no state. */
  code: string;
  /** Full state name ("California"), the raw code as a fallback, or "" for stateless. */
  label: string;
  /** Cities in this state, A–Z. */
  cities: string[];
}

/**
 * Group "City, ST" office strings by state — states A–Z, cities A–Z within —
 * so a long statewide roster reads as a handful of state blocks instead of one
 * tall column of pins. A stateless "City" entry keeps its own headerless group,
 * sorted last.
 */
export function groupOfficesByState(offices: string[]): OfficeStateGroup[] {
  const byCode = new Map<string, string[]>();
  for (const raw of offices) {
    const [cityPart, statePart] = raw.split(",");
    const city = (cityPart ?? "").trim();
    if (city === "") continue;
    const code = (statePart ?? "").trim().toUpperCase();
    const cities = byCode.get(code) ?? [];
    cities.push(city);
    byCode.set(code, cities);
  }

  return [...byCode.entries()]
    .map(([code, cities]) => ({
      code,
      label: stateName(code),
      cities: [...cities].sort((a, b) => a.localeCompare(b)),
    }))
    .sort((a, b) => {
      if (a.code === "" && b.code !== "") return 1;
      if (b.code === "" && a.code !== "") return -1;
      return (a.label || a.code).localeCompare(b.label || b.code);
    });
}

/**
 * The organization's office list — one card shared by the public profile and
 * the signed-in profile, so an office an admin (or the Console, on their
 * behalf) adds shows up for every viewer. Cities are grouped under their state;
 * with `display="states"` (an admin's choice, for a cloud or statewide
 * brokerage) only the state names are shown. Renders nothing when there are
 * no offices.
 *
 * Callers pass the EFFECTIVE display (src/lib/workModel.ts): a cloud brokerage
 * is states only whatever its own choice, and `cloud` adds the one line that
 * says agents join remotely. `cloud` is only ever true together with states.
 */
export default function OfficesCard({
  offices,
  display = "cities",
  cloud = false,
}: {
  offices: string[];
  display?: OfficesDisplay;
  /** Treated as cloud (workModelView().treatedAsCloud). */
  cloud?: boolean;
}) {
  const groups = groupOfficesByState(offices);
  if (groups.length === 0) return null;
  const stateCount = groups.filter((g) => g.code !== "").length;
  const statesOnly = display === "states";

  return (
    <SurfaceCard padding="lg" className="rounded-2xl">
      <div className="mb-3 flex items-baseline justify-between gap-2">
        <h2 className="m-0 text-[13px] font-medium uppercase tracking-[0.06em] text-ink-500">
          {statesOnly ? "Where we operate" : "Offices"}
        </h2>
        {stateCount > 1 && (
          <span className="text-[12px] text-ink-500">
            {stateCount} states
          </span>
        )}
      </div>
      {statesOnly && cloud && (
        <p data-cloud-footprint-line className="m-0 mb-3 text-[13.5px] leading-relaxed text-ink-700">
          {CLOUD_FOOTPRINT_LINE}
        </p>
      )}
      {statesOnly ? (
        <ul className="m-0 grid list-none grid-cols-1 gap-x-8 gap-y-2 p-0 sm:grid-cols-2">
          {groups.flatMap((g) =>
            // A stateless entry has no state to name, so its cities stay.
            (g.code ? [g.label] : g.cities).map((name) => (
              <li key={`${g.code || "_stateless"}:${name}`} className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                <span className="text-[14.5px] font-medium text-ink-900">{name}</span>
              </li>
            )),
          )}
        </ul>
      ) : (
        <ul className="m-0 list-none p-0 sm:columns-2 sm:gap-x-8">
          {groups.map((g) => (
            <li key={g.code || "_stateless"} className="mb-3 break-inside-avoid last:mb-0">
              {g.label && (
                <div className="mb-1 flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                  <span className="text-[14.5px] font-medium text-ink-900">{g.label}</span>
                  {/* Only when it adds information: "Hawaii · 1" beside one city name is noise. */}
                  {g.cities.length > 1 && (
                    <span className="text-[12px] text-ink-500">· {g.cities.length}</span>
                  )}
                </div>
              )}
              <p className={`m-0 text-[13.5px] leading-relaxed text-ink-700 ${g.label ? "pl-5" : ""}`}>
                {g.cities.join(", ")}
              </p>
            </li>
          ))}
        </ul>
      )}
    </SurfaceCard>
  );
}
